/**
 * The browser pre-check of a private key, run before any request (mission 7
 * WP09 T050; FR-001, FR-005; TX.1, TEN.4; research §1.23; credentials-api PUT
 * `private_key`). The server is authoritative; this only saves a round trip
 * and names the problem in the reporter's words.
 *
 * - At most 16,384 bytes of UTF-8.
 * - A `BEGIN … PRIVATE KEY` header (PKCS#8, RSA, EC or OpenSSH).
 * - `Proc-Type: 4,ENCRYPTED` or `BEGIN ENCRYPTED PRIVATE KEY` is passphrase-protected.
 * - An OpenSSH key is decoded far enough to read its cipher name; anything but
 *   `none` is passphrase-protected.
 *
 * Pure: the key text is an argument and a local, never stored or logged.
 */
import type { TextKey } from '@/lib/provisional-copy'

export type KeyCheck = 'ok' | 'tooLarge' | 'encrypted' | 'malformed'

export const KEY_MAX_BYTES = 16_384

const HEADER = /-----BEGIN (RSA |EC |OPENSSH |)PRIVATE KEY-----/
const OPENSSH_BODY = /-----BEGIN OPENSSH PRIVATE KEY-----([\s\S]*?)-----END OPENSSH PRIVATE KEY-----/
const MAGIC = 'openssh-key-v1\0'

export function checkKey(text: string): KeyCheck {
  if (new TextEncoder().encode(text).length > KEY_MAX_BYTES) return 'tooLarge'
  if (/Proc-Type:\s*4,ENCRYPTED/.test(text) || text.includes('BEGIN ENCRYPTED PRIVATE KEY')) return 'encrypted'
  const header = HEADER.exec(text)
  if (!header) return 'malformed'
  return header[1] === 'OPENSSH ' ? opensshCipher(text) : 'ok'
}

/** Reads the length-prefixed cipher name after the openssh-key-v1 magic. */
function opensshCipher(text: string): KeyCheck {
  const body = OPENSSH_BODY.exec(text)?.[1]?.replace(/\s+/g, '')
  if (!body) return 'malformed'
  let blob: string
  try {
    blob = atob(body)
  } catch {
    return 'malformed'
  }
  if (!blob.startsWith(MAGIC) || blob.length < MAGIC.length + 4) return 'malformed'
  let at = MAGIC.length
  let length = 0
  for (let i = 0; i < 4; i++) length = length * 256 + blob.charCodeAt(at++)
  if (at + length > blob.length) return 'malformed'
  return blob.slice(at, at + length) === 'none' ? 'ok' : 'encrypted'
}

const COPY: Record<Exclude<KeyCheck, 'ok'>, TextKey> = {
  tooLarge: 'keyTooLarge',
  encrypted: 'keyEncrypted',
  malformed: 'keyMalformed',
}

/** The register key that names a refusal. */
export function keyCheckCopyKey(result: Exclude<KeyCheck, 'ok'>): TextKey {
  return COPY[result]
}
